import { type SubmitHandler, useForm } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import { SITE_SECTION_TITLE_MAX_LENGTH, SITE_SECTION_TITLE_MIN_LENGTH } from '@gitbook/api-client';
import {
    Description,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    Fieldset,
    Form,
    Label,
    Submit,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';

import { apiClient } from '../../../state';
import { SiteStructureItemList } from './SiteStructureItemList';
import { TitleAndIconField } from './TitleAndIconField';

type SectionFormData = {
    title: string;
    icon: string | null;
};

export interface CreateSiteSectionGroupFormProps {
    organization: api.Organization;
    site: api.Site;
    siteSections: api.SiteSection[];
    parent?: string;
}

export function CreateSiteSectionGroupForm(props: CreateSiteSectionGroupFormProps) {
    const { organization, site, siteSections, parent } = props;

    const form = useForm<SectionFormData>({
        defaultValues: {
            title: '',
            icon: null,
        },
    });
    const [isPending, startDialogAction] = useDialogAction();
    const onSubmit: SubmitHandler<SectionFormData> = async (data) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                await apiClient.siteSectionGroups.addSectionGroupToSite(organization.id, site.id, {
                    title: data.title,
                    icon: data.icon,
                    sections: siteSections.map((section) => section.id),
                    parent,
                });
            }, 'Error while adding site section group');
            close();
        });
    };
    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <Fieldset>
                    <TitleAndIconField
                        form={form}
                        titleName="title"
                        iconName="icon"
                        minLength={SITE_SECTION_TITLE_MIN_LENGTH}
                        maxLength={SITE_SECTION_TITLE_MAX_LENGTH}
                        disabled={isPending}
                        autoFocus
                    >
                        <Label>Title & Icon</Label>
                        <Description>
                            Choose a title and icon that represents this group.
                        </Description>
                    </TitleAndIconField>
                    {siteSections.length > 0 ? (
                        <SiteStructureItemList
                            description="The following sections will be added to this group:"
                            items={siteSections}
                        />
                    ) : null}
                </Fieldset>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Add" />
            </DialogFooter>
        </Form>
    );
}
